import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateCentralResources } from "@/shared/lib/cacheInvalidations";
import { YEATI_LOC_QUERY_KEYS } from "../configs/queryKeys";
import { YeatiLocService } from "../services/yeatiLoc.service";
import { apiErrorMessage } from "../utils/apiErrorMessage";
import { toastDuplicateWarnings } from "../utils/duplicateWarnings";
import type {
  CatalogCollectionCreatePayload,
  CatalogCollectionResponse,
  CatalogCollectionUpdatePayload,
} from "../types/catalog.api.types";

export const useCollections = (enabled = true) =>
  useQuery({
    queryKey: YEATI_LOC_QUERY_KEYS.collections.lists(),
    queryFn: () => YeatiLocService.getCollections(),
    enabled,
  });

export const useCollectionMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: YEATI_LOC_QUERY_KEYS.collections.all });
    // BCK-315 : les collections sont agregees par /context_options et nommees
    // dans les regles de subvention, dont le cache reste sur l'ancien libelle.
    invalidateCentralResources(queryClient, ["subsidy-policies"]);
  };

  const create = useMutation({
    mutationFn: (payload: CatalogCollectionCreatePayload) =>
      YeatiLocService.createCollection(payload),
    onSuccess: () => {
      invalidate();
      toast.success("Collection créée avec succès");
    },
    onError: () => toast.error("Erreur lors de la création de la collection"),
  });

  const update = useMutation({
    mutationFn: (
      { id, payload }: { id: string, payload: CatalogCollectionUpdatePayload },
    ) => YeatiLocService.updateCollection(id, payload),
    onSuccess: () => {
      invalidate();
      toast.success("Collection mise à jour");
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => YeatiLocService.deleteCollection(id),
    onSuccess: () => {
      invalidate();
      toast.success("Collection supprimée");
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  const duplicate = useMutation({
    mutationFn: (id: string) => YeatiLocService.duplicateCollection(id),
    // Jamais rejouée : après un 5xx ou une coupure, la première copie a pu
    // être créée, et un second POST en ferait une deuxième.
    retry: false,
    onSuccess: ({ warnings }) => {
      invalidate();
      // La copie apporte ses produits : le catalogue doit les voir.
      queryClient.invalidateQueries({ queryKey: YEATI_LOC_QUERY_KEYS.products.all });
      toast.success("Collection dupliquée\u00a0: la copie est inactive et ses produits sont en brouillon");
      toastDuplicateWarnings(warnings);
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err) ?? "Erreur lors de la duplication de la collection"),
  });

  // Reorder by ranking the collections in the order of `orderedIds`.
  // Optimistically updates the cache so the table doesn't flicker, then
  // PATCHes each collection in parallel. A single toast on settle -
  // unlike `update`, which would spam one toast per row.
  const reorder = useMutation<
    void,
    Error,
    string[],
    { previous: CatalogCollectionResponse[] | undefined }
  >({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(
        orderedIds.map((id, index) =>
          YeatiLocService.updateCollection(id, { collection: { rank: index + 1 } }),
        ),
      );
    },
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: YEATI_LOC_QUERY_KEYS.collections.lists() });
      const previous = queryClient.getQueryData<CatalogCollectionResponse[]>(
        YEATI_LOC_QUERY_KEYS.collections.lists(),
      );
      if (previous) {
        const byId = new Map(previous.map((c) => [c.id, c]));
        const reordered = orderedIds
          .map((id, index) => {
            const item = byId.get(id);
            return item ? { ...item, rank: index + 1 } : null;
          })
          .filter((c): c is CatalogCollectionResponse => c !== null);
        queryClient.setQueryData(YEATI_LOC_QUERY_KEYS.collections.lists(), reordered);
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          YEATI_LOC_QUERY_KEYS.collections.lists(),
          context.previous,
        );
      }
      toast.error("Erreur lors du réordonnancement");
    },
    onSuccess: () => toast.success("Ordre mis à jour"),
    onSettled: () => invalidate(),
  });

  return {
    create, update, remove, duplicate, reorder,
  };
};
